import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, ArrowLeft, Calendar, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import StarRating from '../components/StarRating';
import TierBadge from '../components/TierBadge';
import RatingPanel, { ScoreBreakdown } from '../components/RatingPanel';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AwardDetailPage() {
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [award, setAward] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState(null);

  const fetchScores = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/awards/${id}/scores`, { withCredentials: true });
      setScores(res.data);
    } catch (e) {
      // scores endpoint is optional — silently ignore
    }
  }, [id]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const response = await axios.get(`${API_URL}/api/awards/${id}`);
        if (alive) setAward(response.data);
      } catch (error) {
        if (alive) toast.error('Failed to load award details');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    fetchScores();
    return () => { alive = false; };
  }, [id, fetchScores]);

  const categoryIcons = {
    hotel: '🏨',
    resort: '🏝️',
    destination: '🌍'
  };

  if (loading) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="h-[50vh] skeleton rounded-2xl mb-8" />
          <div className="space-y-4">
            <div className="h-10 skeleton rounded w-3/4" />
            <div className="h-6 skeleton rounded w-1/2" />
            <div className="h-4 skeleton rounded w-full" />
            <div className="h-4 skeleton rounded w-full" />
          </div>
        </div>
      </main>
    );
  }

  if (!award) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center py-20">
          <h1 className="text-2xl font-semibold text-white mb-4">Award not found</h1>
          <Link to="/awards">
            <Button className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A]">
              Back to Awards
            </Button>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-20 pb-16">
      {/* Hero Image */}
      <div className="relative h-[50vh] sm:h-[60vh]">
        <img
          src={award.image_url || 'https://images.unsplash.com/photo-1582533568805-78a15dcb01b5?w=1200'}
          alt={award.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
        
        {/* Back Button */}
        <Link
          to="/awards"
          className="absolute top-24 left-4 sm:left-8 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          data-testid="back-to-awards"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Awards</span>
        </Link>

        {/* Tier Badge */}
        <div className="absolute top-24 right-4 sm:right-8">
          <TierBadge tier={award.tier} size="lg" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10">
        <div className="bg-[#121212] rounded-2xl border border-white/5 overflow-hidden">
          <div className="p-6 sm:p-10">
            {/* Category */}
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 text-white/70 text-sm mb-4">
              <span>{categoryIcons[award.category] || '🏆'}</span>
              <span className="capitalize">{award.category}</span>
            </span>

            {/* Title */}
            <h1 
              className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-4"
              data-testid="award-title"
            >
              {award.name}
            </h1>

            {/* Location */}
            <div className="flex items-center gap-2 text-white/50 mb-6">
              <MapPin className="w-5 h-5" />
              <span className="text-lg">{award.location}, {award.country}</span>
            </div>

            {/* Rating and Year */}
            <div className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-white/10">
              <div className="flex items-center gap-3">
                <StarRating rating={award.star_rating} size="lg" />
                <span className="text-white/50">{award.star_rating} Stars</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Calendar className="w-5 h-5" />
                <span>{award.year} Winner</span>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="font-['Playfair_Display'] text-xl font-semibold text-white mb-4">
                About
              </h2>
              <p className="text-white/70 leading-relaxed text-lg">
                {award.description}
              </p>
            </div>

            {/* Features */}
            {award.features && award.features.length > 0 && (
              <div className="mb-8">
                <h2 className="font-['Playfair_Display'] text-xl font-semibold text-white mb-4">
                  Features & Amenities
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {award.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-lg bg-white/5"
                    >
                      <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-white/80">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Score breakdown + per-role rating panel */}
            <div className="pt-8 border-t border-white/10 space-y-6">
              <ScoreBreakdown scores={scores} role={user?.role} />

              {isAuthenticated ? (
                <RatingPanel
                  awardId={id}
                  user={user}
                  onScoresUpdate={(s) => setScores((prev) => ({ ...prev, ...s }))}
                />
              ) : (
                <div className="p-5 rounded-xl border border-dashed border-white/10 text-center">
                  <p className="text-white/60 text-sm">
                    <Link to="/login" className="text-[#D4AF37] hover:underline">Sign in</Link>{' '}
                    to rate this award.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
